/**
 * Table List Panel
 *
 * Left sidebar displaying the list of tables in the schema.
 * Allows selecting, adding, and deleting tables.
 */

import React, { useState } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import DialogActions from "@mui/joy/DialogActions";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import type { SchemaBuilderTable } from "../../types";

interface TableListPanelProps {
  tables: SchemaBuilderTable[];
  selectedTableId: string | null;
  onSelectTable: (tableId: string) => void;
  onAddTable: () => void;
  onDeleteTable: (tableId: string) => void | Promise<void>;
}

export function TableListPanel({
  tables,
  selectedTableId,
  onSelectTable,
  onAddTable,
  onDeleteTable,
}: TableListPanelProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<SchemaBuilderTable | null>(
    null,
  );

  const handleDeleteClick = (
    e: React.MouseEvent,
    table: SchemaBuilderTable,
  ) => {
    e.stopPropagation();
    setDeleteConfirm(table);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirm) {
      onDeleteTable(deleteConfirm.tempId);
      setDeleteConfirm(null);
    }
  };

  return (
    <Sheet
      variant="soft"
      sx={{
        width: 260,
        minWidth: 260,
        borderRight: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography level="title-md" startDecorator={<TableChartRoundedIcon />}>
          Tables
        </Typography>
        <Button
          size="sm"
          variant="soft"
          startDecorator={<AddRoundedIcon />}
          onClick={onAddTable}
        >
          Add
        </Button>
      </Box>

      {/* Table List */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {tables.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              color: "text.tertiary",
            }}
          >
            <Typography level="body-sm">No tables yet</Typography>
            <Typography level="body-xs" sx={{ mt: 0.5 }}>
              Click &quot;Add&quot; to create your first table
            </Typography>
          </Box>
        ) : (
          <List size="sm" sx={{ p: 1 }}>
            {tables.map((table) => (
              <ListItem
                key={table.tempId}
                endAction={
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={(e) => handleDeleteClick(e, table)}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ".MuiListItem-root:hover &": {
                        opacity: 1,
                      },
                    }}
                  >
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={selectedTableId === table.tempId}
                  onClick={() => onSelectTable(table.tempId)}
                  sx={{ borderRadius: "sm" }}
                >
                  <ListItemContent>
                    <Typography level="title-sm">
                      {table.displayName || "Untitled Table"}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {table.columns.length} column
                      {table.columns.length !== 1 ? "s" : ""}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon />
            Delete Table
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete &quot;
            {deleteConfirm?.displayName || "this table"}&quot;?
            {deleteConfirm?.id && (
              <Typography level="body-sm" sx={{ mt: 1, color: "danger.500" }}>
                Warning: This table has been saved to the database. Deleting it
                will permanently remove all data stored in this table.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Sheet>
  );
}

export default TableListPanel;
